import React, { useMemo, useState } from 'react';
import {
  IcCheck,
  IcChevronLeft,
  IcChevronRight,
  IcHistory,
  IcBanknote,
  IcMoon,
  IcPhone,
  IcPlus,
  IcFlower,
  IcSun,
  IcClose,
  IcTrash,
  IcUser,
} from '../../theme/icons';

// Mobile adaptation of SoinsCalendarView (owner Studio 2026-08-14): the month
// grid keeps the exact web design full-width; tapping a day opens the day
// panel (the card next to the calendar on the web) as a popup. The panel
// content — sessions list, « Nouvelle réservation » inline form, actions — is
// the same as the web version. Keep both files in sync when the owner tweaks
// the calendar design.

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Same convention as the vente-page soin modal: matin → 09:00, après-midi → 14:00.
const PERIOD_TIME = { matin: '09:00', apresmidi: '14:00' };

// Displayed period is derived back from the stored hour (before noon = matin).
const sessionPeriod = (s) => ((s.scheduled_time || '').slice(11, 13) < '12'
  ? { label: 'Matin', Icon: IcSun, cls: 'bg-amber-50 border-amber-200 text-amber-700' }
  : { label: 'Après-midi', Icon: IcMoon, cls: 'bg-amber-100 border-amber-600 text-amber-700' });

const pad2 = (n) => String(n).padStart(2, '0');
const dayKey = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const todayKey = () => {
  const t = new Date();
  return dayKey(t.getFullYear(), t.getMonth(), t.getDate());
};

const EMPTY_BOOKING = {
  service: '', customer: null, customerQuery: '', customerResults: [],
  showDropdown: false, period: '', payment: 'unpaid', notes: '',
};

const MobileSoinsCalendar = ({ sessions, statusMeta, soinServices, onSearchCustomers, onCreate, onComplete, onCancel, onDelete }) => {
  const now = new Date();
  const [viewMonth, setViewMonth] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [selectedDay, setSelectedDay] = useState(todayKey());
  const [panelOpen, setPanelOpen] = useState(false); // mobile: the day panel is a popup
  // "Nouvelle réservation" opens the fields INSIDE the day popup; Retour discards.
  const [booking, setBooking] = useState(false);
  const [form, setForm] = useState(EMPTY_BOOKING);
  const [submitting, setSubmitting] = useState(false);

  // Group sessions by day, each day sorted by hour.
  const byDay = useMemo(() => {
    const map = {};
    (sessions || []).forEach((s) => {
      const key = (s.scheduled_time || '').slice(0, 10);
      if (!key) return;
      (map[key] = map[key] || []).push(s);
    });
    Object.values(map).forEach((list) => list.sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time)));
    return map;
  }, [sessions]);

  const { year, month } = viewMonth;
  const monthLabel = new Date(year, month, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const tKey = todayKey();
  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  const openDay = (key) => {
    if (key !== selectedDay) closeBooking();
    setSelectedDay(key);
    setPanelOpen(true);
  };
  const closePanel = () => { setPanelOpen(false); closeBooking(); };
  const goToday = () => {
    setViewMonth({ year: now.getFullYear(), month: now.getMonth() });
    openDay(tKey);
  };

  const daySessions = byDay[selectedDay] || [];
  const selectedIsPast = selectedDay < tKey;
  const selectedLabel = new Date(`${selectedDay}T00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  // Chip color = status tint (same palette as the list view pills).
  // Owner Studio exception: completed chips = gray bg + green text, no border.
  const chipCls = (s) => (s.status === 'completed'
    ? 'bg-gray-100 text-green-600'
    : (statusMeta[s.status] || statusMeta.scheduled).cls);

  // ---- Inline booking form (day popup) ----
  const closeBooking = () => { setBooking(false); setForm(EMPTY_BOOKING); };

  const searchCustomers = async (q) => {
    setForm((f) => ({ ...f, customerQuery: q, customer: null, showDropdown: true, customerResults: q.trim() ? f.customerResults : [] }));
    if (!q.trim()) return;
    try {
      const list = await onSearchCustomers(q);
      setForm((f) => ({ ...f, customerResults: Array.isArray(list) ? list : [] }));
    } catch { /* search errors: keep previous results, non-fatal */ }
  };

  const bookingValid = form.service && form.customer && form.period;

  const submitBooking = async () => {
    if (!bookingValid || submitting) return;
    setSubmitting(true);
    // onCreate (parent) does the API call + succès/erreur feedback; true = créé.
    const ok = await onCreate({
      service: parseInt(form.service, 10),
      customer: form.customer.id,
      scheduled_time: `${selectedDay}T${PERIOD_TIME[form.period]}`,
      status: 'scheduled',
      payment_status: form.payment,
      notes: form.notes.trim(),
    });
    setSubmitting(false);
    if (ok) closeBooking();
  };

  return (
    <>
      {/* ---- Month grid (same design as the web version, full width) ---- */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => shiftMonth(-1)} className="h-9 w-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors"><IcChevronLeft size={13} /></button>
            <button type="button" onClick={() => shiftMonth(1)} className="h-9 w-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors"><IcChevronRight size={13} /></button>
          </div>
          <span className="text-base font-bold text-gray-800 capitalize">{monthLabel}</span>
          {/* « Aujourd'hui » = secondaire (Boutons §2). */}
          <button type="button" onClick={goToday} className="btn-s px-3 py-1.5 text-xs transition-colors">Aujourd'hui</button>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-center text-[11px] font-semibold text-gray-400 py-1">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} className="min-h-[86px]" />;
            const key = dayKey(year, month, d);
            const list = byDay[key] || [];
            const isToday = key === tKey;
            const isSelected = key === selectedDay && panelOpen;
            const isPast = key < tKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => openDay(key)}
                className={`min-h-[86px] rounded-xl border p-1.5 flex flex-col items-stretch gap-1 text-left transition-all ${
                  isSelected
                    ? 'border-gray-900 ring-2 ring-gray-200 bg-white'
                    : list.length > 0
                      ? 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm'
                      : 'border-gray-100 bg-gray-50/50 hover:border-gray-300 hover:bg-white'
                }`}
              >
                <span className={`self-start text-xs font-semibold h-5 min-w-[20px] px-1 rounded-full flex items-center justify-center ${
                  isToday ? 'bg-gray-900 text-white' : isPast ? 'text-gray-400' : 'text-gray-700'
                }`}>{d}</span>
                {list.slice(0, 2).map((s) => (
                  <span key={s.id} className={`px-1 py-0.5 rounded-md text-[10px] leading-tight font-medium truncate ${chipCls(s)}`}>
                    {s.service_name}
                  </span>
                ))}
                {list.length > 2 && (
                  <span className="text-[10px] font-semibold text-gray-400 px-1 truncate min-w-0">+{list.length - 2}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- Day panel: same card as the web version, shown as a popup ---- */}
      {panelOpen && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={closePanel}>
          <div
            className="w-full max-w-md max-h-[85vh] flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Entête de modale = aplat encre, titre blanc + ✕ (Composants §10). */}
            <div className="modal-head gap-2 shrink-0">
              <p className="modal-title text-sm capitalize">{selectedLabel}</p>
              <div className="flex items-center gap-2">
                {daySessions.length > 0 && (
                  <span className="h-6 min-w-[24px] px-1.5 rounded-full bg-white text-gray-900 text-xs font-bold flex items-center justify-center">{daySessions.length}</span>
                )}
                <button type="button" onClick={closePanel} aria-label="Fermer" className="h-8 w-8 rounded-lg hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors"><IcClose size={13} /></button>
              </div>
            </div>

            {booking && !selectedIsPast ? (
              /* Fields de la nouvelle réservation — écrits directement dans la carte du jour. */
              <div className="p-4 space-y-3 overflow-y-auto">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Soin <span className="req">*</span></label>
                  <select
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className="inp w-full px-3 py-2.5 text-sm"
                  >
                    <option value="">Sélectionner un soin</option>
                    {(soinServices || []).map((s) => (
                      <option key={s.id} value={s.id}>{s.name} — {(parseFloat(s.price) || 0).toFixed(3)} DT</option>
                    ))}
                  </select>
                  {(soinServices || []).length === 0 && (
                    <p className="mt-1 text-xs text-gray-400">Aucun soin disponible dans le catalogue.</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Client <span className="req">*</span></label>
                  <div className="relative">
                    <IcUser className="absolute left-3 top-3 text-gray-400 z-10" size={12} />
                    <input
                      type="text"
                      value={form.customer ? `${form.customer.first_name || ''} ${form.customer.last_name || ''}`.trim() : form.customerQuery}
                      onChange={(e) => searchCustomers(e.target.value)}
                      onFocus={() => setForm((f) => ({ ...f, showDropdown: true }))}
                      placeholder="Rechercher un client..."
                      className="inp w-full pl-9 pr-9 py-2.5 text-sm"
                    />
                    {form.customer && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, customer: null, customerQuery: '', customerResults: [] }))} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"><IcClose size={12} /></button>
                    )}
                    {form.showDropdown && !form.customer && form.customerResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-44 overflow-y-auto">
                        {form.customerResults.map((c) => (
                          <div key={c.id} onClick={() => setForm((f) => ({ ...f, customer: c, showDropdown: false }))} className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0">
                            <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name || ''}</p>
                            {c.phone && <p className="text-xs text-gray-400">{c.phone}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Période <span className="req">*</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {[['matin', 'Matin', IcSun], ['apresmidi', 'Après-midi', IcMoon]].map(([p, label, Icon]) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setForm({ ...form, period: p })}
                        className={`py-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${form.period === p ? 'border-blue-700 bg-blue-100 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        <Icon size={12} /> {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Paiement</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setForm({ ...form, payment: 'paid' })} className={`py-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${form.payment === 'paid' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <IcBanknote size={12} /> Payé
                    </button>
                    <button type="button" onClick={() => setForm({ ...form, payment: 'unpaid' })} className={`py-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${form.payment === 'unpaid' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <IcHistory size={12} /> Payer plus tard
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Note</label>
                  <textarea
                    rows="2"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Détails du rendez-vous..."
                    className="inp w-full px-3 py-2.5 text-sm"
                  />
                </div>
              </div>
            ) : daySessions.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <IcFlower className="mx-auto text-3xl text-gray-200 mb-2" />
                <p className="text-sm text-gray-400">Aucun soin ce jour.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 overflow-y-auto">
                {daySessions.map((s) => {
                  const meta = statusMeta[s.status] || statusMeta.scheduled;
                  const period = sessionPeriod(s);
                  return (
                    <div key={s.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{s.service_name}</p>
                        {Number(s.final_price) > 0 && (
                          <span className="text-sm font-bold text-gray-800 tabular-nums flex-shrink-0">{Number(s.final_price).toFixed(3)} DT</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border inline-flex items-center gap-1 ${period.cls}`}>
                          <period.Icon size={9} /> {period.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${meta.cls}`}>{meta.label}</span>
                      </div>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                        <p className="text-xs text-gray-600 inline-flex items-center gap-1 min-w-0"><IcUser size={9} className="text-gray-400 flex-shrink-0" /> <span className="truncate">{s.customer_name}</span></p>
                        {s.customer_phone && (
                          <a href={`tel:${s.customer_phone}`} className="text-xs text-gray-600 hover:text-blue-600 inline-flex items-center gap-1 tabular-nums transition-colors"><IcPhone size={9} className="text-gray-400" /> {s.customer_phone}</a>
                        )}
                      </div>
                      {s.notes && <p className="text-[11px] text-gray-400 mt-1 italic truncate">“{s.notes}”</p>}
                      <div className="flex items-center gap-1.5 mt-2">
                        {/* Aligné sur la carte web (SoinsCalendarView) : Supprimer =
                            Destructif bordé (.btn-db) isolé à gauche via mr-auto ·
                            Annuler = .btn-g · Terminé = Primaire vert (.btn-p) au bord droit. */}
                        <button onClick={() => onDelete(s)} aria-label="Supprimer" title="Supprimer" className="btn-db px-2.5 py-1 text-[11px] gap-1 mr-auto"><IcTrash size={9} /></button>
                        {(s.status === 'scheduled' || s.status === 'in_progress') && (
                          <button onClick={() => onCancel(s)} className="btn-g px-2.5 py-1 text-[11px] gap-1"><IcClose size={9} /> Annuler</button>
                        )}
                        {s.status === 'scheduled' && (
                          <button onClick={() => onComplete(s)} className="btn-p px-2.5 py-1 text-[11px] gap-1"><IcCheck size={9} /> Terminé</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Booking in the past stays blocked — no CTA (ni formulaire) on past days. */}
            {!selectedIsPast && (
              <div className="p-3 border-t border-gray-100 shrink-0">
                {booking ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closeBooking}
                      className="btn-g flex-1 py-2.5 rounded-xl text-sm transition-colors"
                    >
                      <IcChevronLeft size={10} /> Retour
                    </button>
                    {/* Réserver conclut la saisie = primaire vert (désactivé : opacité 45 %). */}
                    <button
                      type="button"
                      onClick={submitBooking}
                      disabled={!bookingValid || submitting}
                      className="btn-p flex-1 py-2.5 rounded-xl text-sm transition-colors"
                    >
                      <IcCheck size={11} /> {submitting ? 'Réservation...' : 'Réserver'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setBooking(true)}
                    className="btn-add-block py-2.5 rounded-xl gap-2 text-sm transition-colors"
                  >
                    <IcPlus size={11} /> Ajouter une réservation
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MobileSoinsCalendar;
